import { useEffect, useState } from "react";
import { Plus, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, dt } from "@/api";

const ROLES = [
  { v: "admin", l: "Yönetici" }, { v: "sales", l: "Satış" }, { v: "operations", l: "Operasyon" },
  { v: "finance", l: "Finans" }, { v: "supplier", l: "Tedarikçi" }, { v: "customer", l: "Müşteri" },
];
const EMPTY = { email: "", password: "", name: "", role: "sales", party_id: "" };

export default function Users() {
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/parties").then((r) => setParties(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", { ...form, party_id: form.party_id || null });
      toast.success("Kullanıcı oluşturuldu");
      setForm(EMPTY); setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const toggle = async (u) => {
    try {
      await api.patch(`/users/${u.id}/status`, null, { params: { active: !u.is_active } });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`${u.email} için yeni şifre (en az 8 karakter):`);
    if (!pw) return;
    if (pw.length < 8) return toast.error("Şifre en az 8 karakter olmalı");
    try {
      await api.post(`/users/${u.id}/reset-password`, { new_password: pw });
      toast.success(`${u.email} şifresi sıfırlandı`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Yetkilendirme</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Kullanıcılar</h1>
        </div>
        <button className="brut-btn" onClick={() => setOpen(!open)} data-testid="new-user-button">
          <Plus className="h-4 w-4" /> Yeni Kullanıcı
        </button>
      </div>

      {open && (
        <form onSubmit={save} className="brut grid animate-fade-up grid-cols-1 gap-3 p-4 sm:grid-cols-3" data-testid="user-form">
          <input required placeholder="Ad Soyad" className="brut-input" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
          <input required type="email" placeholder="E-posta" className="brut-input" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
          <input required type="password" placeholder="Şifre" className="brut-input" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
          <select className="brut-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
          <select className="brut-input" value={form.party_id} onChange={(e) => setForm({ ...form, party_id: e.target.value })} data-testid="user-party-select">
            <option value="">Cari bağlantısı (müşteri/tedarikçi için)</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="brut-btn" disabled={busy} data-testid="user-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
          </button>
        </form>
      )}

      <div className="brut overflow-x-auto" data-testid="users-table">
        <table className="dense w-full">
          <thead><tr><th>Ad</th><th>E-posta</th><th>Rol</th><th>Cari</th><th>Oluşturma</th><th className="!text-right">Durum</th><th className="!text-right">Şifre</th></tr></thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={u.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`user-row-${u.id}`}>
                <td className="font-semibold">{u.name}</td>
                <td className="font-mono text-xs">{u.email}</td>
                <td>{ROLES.find((r) => r.v === u.role)?.l || u.role}</td>
                <td className="text-xs">{parties.find((p) => p.id === u.party_id)?.name || "-"}</td>
                <td className="text-xs">{dt(u.created_at)}</td>
                <td className="text-right">
                  <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.id}`}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase ${u.is_active ? "border-green-200 bg-green-50 text-green-700" : "border-zinc-300 bg-zinc-100 text-zinc-500"}`}>
                    {u.is_active ? "Aktif" : "Pasif"}
                  </button>
                </td>
                <td className="text-right">
                  <button onClick={() => resetPassword(u)} className="text-zinc-400 hover:text-klein"
                    data-testid={`reset-password-${u.id}`} title="Şifre sıfırla">
                    <KeyRound className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
